import React from 'react';
import { LucideIcon } from 'lucide-react';

interface SidebarRailItemProps {
  label: string;
  icon: LucideIcon;
  /** Optional theme-aware image icon — overrides `icon` when present */
  iconImage?: { light: string; dark: string };
  isDark?: boolean;
  isActive?: boolean;
  isCollapsed: boolean;
  onClick: () => void;
  variant?: 'default' | 'danger';
  /** Optional count badge on the icon (e.g. unseen announcements). Hidden when 0/undefined. */
  badgeCount?: number;
  /** Optional `data-tour` anchor slug for guided tutorials. */
  dataTour?: string;
}

const SidebarRailItem: React.FC<SidebarRailItemProps> = ({
  label,
  icon: Icon,
  iconImage,
  isDark,
  isActive = false,
  isCollapsed,
  onClick,
  variant = 'default',
  badgeCount,
  dataTour,
}) => {
  const showBadge = typeof badgeCount === 'number' && badgeCount > 0;
  const baseClass =
    variant === 'danger' ? 'cgm-rail-item cgm-rail-item--danger' : 'cgm-rail-item';
  const activeClass = isActive ? 'cgm-rail-item--active' : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClass} ${activeClass}`}
      aria-label={label}
      data-collapsed={isCollapsed || undefined}
      data-label={label}
      data-tour={dataTour}
    >
      <span className="cgm-rail-item-icon relative">
        {iconImage ? (
          <img
            src={isDark ? iconImage.dark : iconImage.light}
            alt=""
            className="w-5 h-5 object-contain cgm-rail-img-icon"
          />
        ) : (
          <Icon className="w-5 h-5" />
        )}
        {showBadge && (
          badgeCount === 1 ? (
            <span
              className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500"
              aria-hidden
            />
          ) : (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-none font-semibold flex items-center justify-center"
              aria-hidden
            >
              {badgeCount! > 9 ? '9+' : badgeCount}
            </span>
          )
        )}
      </span>
      <span className="cgm-rail-item-label" aria-hidden={isCollapsed}>
        {label}
      </span>
    </button>
  );
};

export default SidebarRailItem;
